// @mention model for frame comments. A mention is ordinary text — the
// highlight is derived by scanning the text for '@' + display name, so any
// edit that breaks the match makes the highlight dissolve. Nothing here is a
// token or widget; the textarea stays a plain textarea.

import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { UserContact } from 'dragonmouse-shared/interfaces';
import { RootReducer } from 'redux/reducers';
import { selectSceneUserContacts } from 'redux/db/reducers';

//------------------------------------------------------------------------------------------------
// MENTIONABLE USERS
//------------------------------------------------------------------------------------------------

export type MentionUser = UserContact & { isSelf: boolean };

// The scene's user contacts from Redux (the same roster the sharing UI is
// built on), viewed for mentions: decorated with viewer identity, signed-in
// user first.
export const useMentionUsers = (): MentionUser[] => {
	const { authUser, sceneUserContacts } = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
			sceneUserContacts: selectSceneUserContacts(r.db),
		}),
		shallowEqual
	);

	return useMemo(() => {
		const mentionUsers: MentionUser[] = sceneUserContacts.map((contact) => ({
			...contact,
			isSelf: contact.id === authUser?.id,
		}));
		mentionUsers.sort((a, b) => Number(b.isSelf) - Number(a.isSelf));
		return mentionUsers;
	}, [sceneUserContacts, authUser?.id]);
};

//------------------------------------------------------------------------------------------------
// TEXT SCANNING
//------------------------------------------------------------------------------------------------

export interface MentionRange {
	start: number; // index of the '@'
	end: number; // exclusive
	mentionUser: MentionUser;
}

const isBoundary = (text: string, index: number) => index === 0 || /\s/.test(text[index - 1]);

// All non-overlapping '@Display Name' occurrences for the given users. Longer
// names win overlaps so 'Alex Rivera-Smith' isn't half-claimed by 'Alex Rivera'.
export const computeMentionRanges = (text: string, mentionUsers: MentionUser[]): MentionRange[] => {
	const candidates: MentionRange[] = [];
	const seen = new Set<string>();
	for (const mentionUser of mentionUsers) {
		if (seen.has(mentionUser.id) || !mentionUser.displayName) continue;
		seen.add(mentionUser.id);
		const needle = `@${mentionUser.displayName}`;
		let from = 0;
		while (true) {
			const start = text.indexOf(needle, from);
			if (start === -1) break;
			from = start + 1;
			if (!isBoundary(text, start)) continue;
			candidates.push({ start, end: start + needle.length, mentionUser });
		}
	}
	candidates.sort((a, b) => a.start - b.start || b.end - a.end);
	const ranges: MentionRange[] = [];
	for (const c of candidates) {
		const last = ranges[ranges.length - 1];
		if (!last || c.start >= last.end) ranges.push(c);
	}
	return ranges;
};

// The '@…' the caret is currently completing, if any. Anchored at an '@' that
// sits on a word boundary, isn't already part of a completed mention, and has
// no newline between it and the caret.
export interface MentionSession {
	anchor: number; // index of the '@'
	query: string;
}

export const findMentionSession = (
	text: string,
	caret: number | null,
	ranges: MentionRange[]
): MentionSession | null => {
	if (caret === null || caret < 1) return null;
	const from = Math.max(0, caret - 80);
	for (let i = caret - 1; i >= from; i--) {
		const ch = text[i];
		if (ch === '\n') return null;
		if (ch !== '@') continue;
		if (!isBoundary(text, i)) return null;
		if (ranges.some((r) => i >= r.start && i < r.end)) return null;
		return { anchor: i, query: text.slice(i + 1, caret) };
	}
	return null;
};

export const filterMentionUsers = (mentionUsers: MentionUser[], query: string): MentionUser[] => {
	const q = query.trim().toLowerCase();
	if (!q) return mentionUsers;
	return mentionUsers.filter((mentionUser) => {
		const name = mentionUser.displayName.toLowerCase();
		const email = mentionUser.email.toLowerCase();
		return (
			name.startsWith(q) ||
			name.split(/\s+/).some((word) => word.startsWith(q)) ||
			email.startsWith(q) ||
			email.split('@')[0].startsWith(q)
		);
	});
};
